import { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2, Repeat } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioSegments } from "@/components/ui/radio-segments";
import { Textarea } from "@/components/ui/textarea";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { muscleLabel } from "./lib/format";
import { primaryMuscles, swapOptions } from "./lib/planEdit";
import { JOINTS, jointPlace as where, jointWords } from "./lib/sessionSets";

// backend/training/constants.py PainReport.Side.
const SIDES = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "both", label: "Both" },
];
// The API's cap on a pain note.
const NOTE_MAX = 300;

// The report's answer replaces Report pain in place with the swap options,
// Skip and Keep: the second click of a double-click on Report pain must not
// press one of them (house rule, #325). Keyboard (detail 0) still acts.
const once = (act) => (event) => {
  if (event.detail > 1) return;
  act();
};
const holdFocus = (event) => {
  if (event.detail > 1) event.preventDefault();
};
// A footer action whose label carries an exercise name wraps at 390 px.
const WRAPS = "h-auto min-h-11 whitespace-normal py-2 md:min-h-9";

function PainSteps({
  exercise,
  item,
  sessionId,
  exercises,
  settings,
  exclude,
  remaining,
  painful,
  closed,
  onSwap,
  onSkip,
  onReported,
  onClose,
  busy,
  setBusy,
  jointRef,
}) {
  const id = useId();
  const name = exercise.name ?? exercise.key;
  const [joint, setJoint] = useState("");
  const [side, setSide] = useState(undefined);
  const [note, setNote] = useState("");
  const [jointError, setJointError] = useState(null);
  const [error, setError] = useState(null);
  const [reported, setReported] = useState(null); // {joint, side}
  // mono's note when this joint has now hurt on two days in two weeks (f8)
  const [repeat, setRepeat] = useState([]);
  const afterRef = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const own = exercise.joints ?? [];
  const others = JOINTS.filter((j) => !own.includes(j.value));
  // Every joint a swap must spare: the one just reported, and any other
  // that hurts today (W0: never a swap into a painful joint — as the ⋯
  // picker), each with its side, in words.
  const sparing = useMemo(() => {
    if (!reported) return [];
    const rest = [...(painful ?? new Map())].filter(([joint]) => joint !== reported.joint);
    return [[reported.joint, reported.side], ...rest];
  }, [reported, painful]);
  const spared = useMemo(() => {
    if (!reported) return [];
    const byKey = new Map((exercises ?? []).map((e) => [e.key, e]));
    const full = byKey.get(exercise.key) ?? exercise;
    const hurts = new Set(sparing.map(([joint]) => joint));
    return swapOptions(full, exercises, settings, { exclude }).options.filter(
      (option) => !(option.joints ?? []).some((joint) => hurts.has(joint)),
    );
  }, [reported, sparing, exercise, exercises, settings, exclude]);

  const report = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!joint) {
      setJointError("Choose the joint that hurts.");
      return;
    }
    setBusy("report");
    setError(null);
    const chosenSide = side ?? "both";
    try {
      const response = await trainingApi.pain({
        joint,
        side: chosenSide,
        exercise: exercise.key,
        ...(sessionId ? { session: sessionId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onReported?.({ joint, side: chosenSide });
      if (!mounted.current) return;
      setRepeat(response?.data?.notices ?? []);
      setReported({ joint, side: chosenSide });
      setTimeout(() => afterRef.current?.focus(), 0);
    } catch (err) {
      if (mounted.current) setError(apiErrorMessage(err, "Couldn't save the pain report."));
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const act = async (kind, work) => {
    setBusy(kind);
    setError(null);
    try {
      const result = await work();
      if (!mounted.current) return;
      if (result?.ok === false) setError(result.error ?? "Couldn't change the plan.");
      else onClose();
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  if (!reported) {
    return (
      <form onSubmit={report} noValidate className="flex min-h-0 flex-1 flex-col">
        <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-joint`}>Joint</Label>
            <NativeSelect
              ref={jointRef}
              id={`${id}-joint`}
              value={joint}
              className="h-11 md:h-9"
              aria-invalid={jointError ? true : undefined}
              aria-describedby={jointError ? `${id}-joint-error` : undefined}
              disabled={Boolean(busy)}
              onChange={(event) => {
                setJoint(event.target.value);
                setJointError(null);
              }}
            >
              <option value="">Choose the joint</option>
              {own.length > 0 && (
                <optgroup label={`${name} loads`}>
                  {JOINTS.filter((j) => own.includes(j.value)).map((j) => (
                    <option key={j.value} value={j.value}>
                      {j.label}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label={own.length ? "Other joints" : "Joints"}>
                {others.map((j) => (
                  <option key={j.value} value={j.value}>
                    {j.label}
                  </option>
                ))}
              </optgroup>
            </NativeSelect>
            {jointError && (
              <p id={`${id}-joint-error`} className="m-0 text-sm text-destructive">
                {jointError}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <RadioSegments
              legend="Side"
              options={SIDES}
              value={side}
              onChange={setSide}
              disabled={Boolean(busy)}
            />
            <p className="m-0 text-xs text-muted-foreground">Not sure, or both? Leave it — it&apos;s saved as both.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-note`}>Note (optional)</Label>
            <Textarea
              id={`${id}-note`}
              value={note}
              maxLength={NOTE_MAX}
              placeholder="e.g. a pinch at the bottom of the press"
              className="max-h-48 min-h-20"
              aria-describedby={`${id}-note-help`}
              disabled={Boolean(busy)}
              onChange={(event) => setNote(event.target.value)}
            />
            <p id={`${id}-note-help`} className="m-0 text-xs text-muted-foreground">
              For you — it shows on this session&apos;s page in History. Up to {NOTE_MAX} characters.
            </p>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter className="border-t px-6 py-4">
          <Button type="button" variant="outline" className="h-11 md:h-9" disabled={Boolean(busy)} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" className="h-11 md:h-9" disabled={Boolean(busy)}>
            {busy === "report" && <Loader2 className="animate-spin" aria-hidden="true" />}
            {busy === "report" ? "Saving…" : "Report pain"}
          </Button>
        </DialogFooter>
      </form>
    );
  }

  const place = where(reported.joint, reported.side);
  const alsoPlaces = sparing.slice(1).map(([joint, side]) => where(joint, side));
  const also = alsoPlaces.length ? ` and ${alsoPlaces.join(" and ")}` : "";
  // a closed session's plan isn't changed any more (as its cards' ⋯ menus)
  const canStop = Boolean(item) && remaining > 0 && !closed;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
        <p ref={afterRef} tabIndex={-1} role="status" className="m-0 outline-none">
          Saved: pain in {place}. Exercises that load your {jointWords(reported.joint)} are left out
          of your plans for 7 days — unless a check-in on a later day says it&apos;s fine.
        </p>
        {canStop ? (
          <>
            <p className="m-0 font-medium">
              Stop {name} here ({remaining} {remaining === 1 ? "set" : "sets"} left).
              {spared.length > 0 ? ` Swap them to one that spares it${also}:` : ""}
            </p>
            {spared.length > 0 ? (
              <ul aria-label={`Swaps that spare ${place}${also}`} className="m-0 flex list-none flex-col gap-1.5 p-0">
                {spared.map((option) => {
                  const detailId = `${id}-${option.key}`;
                  return (
                    <li key={option.key}>
                      <button
                        type="button"
                        aria-label={`Swap to ${option.name}`}
                        aria-describedby={detailId}
                        disabled={Boolean(busy)}
                        className="flex min-h-11 w-full items-center gap-2 rounded-md border border-input px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50 md:min-h-9"
                        onMouseDown={holdFocus}
                        onClick={once(() => act("swap", () => onSwap(option)))}
                      >
                        <Repeat aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                        <span className="flex flex-col">
                          <span className="text-sm font-medium">{option.name}</span>
                          <span id={detailId} className="text-xs text-muted-foreground">
                            {primaryMuscles(option).map(muscleLabel).join(", ") || "—"}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="m-0 text-sm">
                No swap that spares {place}
                {also} with your equipment — skip it today.
              </p>
            )}
          </>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">
            {closed
              ? "This session has closed, so its plan stays as it is — nothing to stop."
              : item
                ? `Every planned set of ${name} is logged — nothing left to stop.`
                : `${name} isn't in today's plan, so there's nothing to stop.`}
          </p>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {/* Walkthrough 6: "get it looked at if it keeps up" once the same
            joint has hurt on two days in two weeks — mono's words, said as
            it becomes true (f8). Before that, only when not to wait. */}
        {repeat.length > 0 ? (
          repeat.map((text) => (
            <p key={text} className="m-0 text-sm font-medium">
              {text}
            </p>
          ))
        ) : (
          <p className="m-0 text-xs text-muted-foreground">
            Swelling, numbness or pain that spreads? Get it looked at right away.
          </p>
        )}
      </div>
      <DialogFooter className="border-t px-6 py-4">
        {/* With sets left, this keeps them — say so (walkthrough 6: stop it
            is the main action); with none, it's just Done. */}
        <Button
          type="button"
          variant="outline"
          className={WRAPS}
          disabled={Boolean(busy)}
          onMouseDown={holdFocus}
          onClick={once(onClose)}
        >
          {canStop ? "Keep it in today's plan" : "Done"}
        </Button>
        {canStop && (
          <Button
            type="button"
            variant="default"
            className={WRAPS}
            disabled={Boolean(busy)}
            onMouseDown={holdFocus}
            onClick={once(() => act("skip", onSkip))}
          >
            {busy === "skip" && <Loader2 className="animate-spin" aria-hidden="true" />}
            {busy === "skip" ? "Saving…" : `Skip the rest of ${name} today`}
          </Button>
        )}
      </DialogFooter>
    </div>
  );
}

PainSteps.propTypes = {
  exercise: PropTypes.object.isRequired,
  item: PropTypes.object,
  sessionId: PropTypes.number,
  exercises: PropTypes.arrayOf(PropTypes.object).isRequired,
  settings: PropTypes.object,
  exclude: PropTypes.arrayOf(PropTypes.string).isRequired,
  remaining: PropTypes.number.isRequired,
  closed: PropTypes.bool,
  painful: PropTypes.instanceOf(Map),
  onSwap: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onReported: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  busy: PropTypes.string,
  setBusy: PropTypes.func.isRequired,
  jointRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};

/**
 * Pain (walkthrough 6), from any exercise, before or after any set: which
 * joint and side, and an optional note → POST pain/. Then, if the exercise
 * has sets left, stop it: swap the rest to an exercise for the same
 * muscles that doesn't load that joint and fits the equipment (best
 * first), or skip the rest today — or say plainly that nothing spares it.
 *
 * `onSwap(exercise)` and `onSkip()` change the session's plan and resolve
 * to `{ok: true}` or `{ok: false, error}`; the dialog closes on ok and
 * shows the error otherwise. `onReported({joint, side})` says a report was
 * saved (the session warns on its other exercises that load that joint).
 *
 * No 16 px X in the corner: the footer's 44 px buttons are the way out.
 */
function PainDialog({
  open,
  onOpenChange,
  exercise,
  item = null,
  sessionId,
  exercises,
  settings,
  exclude = [],
  remaining = 0,
  painful = null,
  closed = false,
  onSwap,
  onSkip,
  onReported,
  onCloseAutoFocus,
}) {
  const [busy, setBusy] = useState(null);
  const jointRef = useRef(null);
  const close = () => {
    if (!busy) onOpenChange(false);
  };
  const name = exercise?.name ?? exercise?.key ?? "";
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      {/* data-training: portalled out of the layout's wrapper, so it
          carries the scope that keeps its muted text AA (tailwind.css). */}
      <DialogContent
        data-training=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          jointRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">Pain in {name}</DialogTitle>
          <DialogDescription>
            Sharp, in a joint, one side, or getting worse? Report it — that&apos;s not the burn of
            a hard set. Exercises that load the joint are left out for 7 days.
          </DialogDescription>
        </DialogHeader>
        {exercise && (
          <PainSteps
            exercise={exercise}
            item={item}
            sessionId={sessionId}
            exercises={exercises}
            settings={settings}
            exclude={exclude}
            remaining={remaining}
            painful={painful}
            closed={closed}
            onSwap={onSwap}
            onSkip={onSkip}
            onReported={onReported}
            onClose={() => onOpenChange(false)}
            busy={busy}
            setBusy={setBusy}
            jointRef={jointRef}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

PainDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  /** The exercise that hurt (TrainingExercise). */
  exercise: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string,
    joints: PropTypes.arrayOf(PropTypes.string),
  }),
  /** Its plan item, when the plan has it (so it can be stopped). */
  item: PropTypes.object,
  /** The session the pain happened in. */
  sessionId: PropTypes.number,
  /** The catalog, for swaps. */
  exercises: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** TrainingSettings — the equipment decides the swaps. */
  settings: PropTypes.object,
  /** Exercise keys already in the plan (never offered). */
  exclude: PropTypes.arrayOf(PropTypes.string),
  /** Sets of it still to do. */
  remaining: PropTypes.number,
  /** The joints that hurt today (`Map(joint → side)`): no swap loads one. */
  painful: PropTypes.instanceOf(Map),
  /** The session has closed: the report is saved, its plan isn't changed (no swap, no skip). */
  closed: PropTypes.bool,
  onSwap: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  /** A report was saved: `{joint, side}`. */
  onReported: PropTypes.func,
  /** Where focus goes when it closes (default: whatever opened it). */
  onCloseAutoFocus: PropTypes.func,
};

export default PainDialog;
